import {
  Input,
  OnInit,
  Output,
  Component,
  ViewChild,
  ElementRef,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  signal,
  afterNextRender,
  inject,
  NgZone,
} from '@angular/core';
import { from, Observable, of, Subject } from 'rxjs';

import {
  ngHelper,
  ISchedule,
  CustomComponent,
  IOutletScheduleDay,
  IOutletDelivery,
  OUTLET_SCHEDULE_TYPES,
  IFormattedOutletSchedule,
  OUTLET_FORMATTED_SCHEDULE_TYPES,
  UiContextService,
  IScheduleOverride,
} from '@cakeiteasy/web-utils';
import { DeliveryCalendar } from '@cakeiteasy/web-ui';
import moment from 'moment';
import { DeliveryAvailabilityService } from '../../services/delivery-availability.service';
import { DeliveryUiHelperService } from '../../services/delivery-ui-helper.service';
import {
  ICartDailyStockSoldOutProductType,
  ICartDeliveryInfoProductType,
} from '../../../cart/models/delivery/i-cart-delivery-info';
import { take } from 'rxjs/operators';

@Component({
  standalone: false,
  selector: 'pickup-outlet',
  templateUrl: './pickup-outlet.component.html',
  styleUrls: ['./pickup-outlet.component.scss'],
})
export class PickupOutletComponent extends CustomComponent implements OnInit, OnChanges {
  @Input() outlet: any;
  @Input() bakeryId: number;
  @Input() containerRef: string | number | ElementRef | HTMLElement = null;
  @Input() scheduleType: string = OUTLET_SCHEDULE_TYPES.PICKUP_SCHEDULE;
  /**Translation key for the confirm button — which step it says it leads to.*/
  @Input() nextBtnTextKey = 'orderProcess.delivery.goToPayment';
  @Input() nextBtnView = 'btn-pink';
  @Input() fullCapacityDays: { begin: string; end: string }[];
  @Input() availableProductTypes: ICartDeliveryInfoProductType[] = [];
  @Input() dailyStockSoldOutProductTypes: ICartDailyStockSoldOutProductType[] = [];
  @Input() scheduleOverrides: IScheduleOverride[] = [];
  @Input() orderId: number;
  @Input() isAsapAvailable: boolean;

  @Input() isWaitUntilOutletSelect = false;

  @Input() set isOutletSelected(value: { isOutletSelected: boolean }) {
    this.isOutletSelectedFlag.set(value.isOutletSelected);
  }

  @Output() onOutletChange = new EventEmitter<any>();
  @Output() onMapRequested = new EventEmitter<IOutletDelivery>();
  @Output() onNext = new EventEmitter<any>();
  @Output() onAsapSelected = new EventEmitter<any>();

  @ViewChild('rangeDropDown', { static: false }) rangeDropDown: any;
  @ViewChild('datePickerLabel', { static: false }) datePickerLabel: ElementRef<HTMLLabelElement>;

  public timeRanges: string[];
  public selectedTimeRange: string;
  public selectedDate: Date;
  public fieldChange$: Subject<any> = new Subject<any>();
  public dp: any;

  public skeletonPromise: Promise<any> = null;
  public schedule: ISchedule<IOutletScheduleDay>;
  public formattedSchedule: IFormattedOutletSchedule[];
  public isOutletSelectedFlag = signal(false);
  public asapTime = signal<string | null>(null);
  public calendar = signal<DeliveryCalendar>(null);
  public isCalendarLoading = signal(false);

  private _ngZone = inject(NgZone);

  constructor(
    private _deliveryAvailability: DeliveryAvailabilityService,
    private _uiContext: UiContextService,
    private _deliveryUi: DeliveryUiHelperService,
  ) {
    super();
    this._subscribe();
  }

  async ngOnChanges(changes: SimpleChanges) {
    ngHelper.ifChanged(changes, 'isAsapAvailable', async () => {
      await this.initAsapAvailability();
    });
  }

  async ngOnInit() {
    this.selectedDate = this.outlet.date ? this.outlet.date : null;
    this.selectedTimeRange = this.outlet.timeRange ? this.outlet.timeRange : '';

    this.schedule = this.outlet[this.scheduleType];
    this.formattedSchedule = this.outlet[OUTLET_FORMATTED_SCHEDULE_TYPES[this.scheduleType]];

    await (this.skeletonPromise = this.loadCalendar(this._getCalendarRequestDate()));
    this._tryToDatepickerScroll();
  }

  public changeDate(newDate: Date | string | null) {
    let normalizedDate: Date | null = newDate as Date | null;

    if (typeof newDate === 'string') {
      const parsedDate = moment(newDate, 'YYYY-MM-DD', true);
      normalizedDate = parsedDate.isValid() ? parsedDate.toDate() : null;
    }

    this.selectedDate = normalizedDate;

    if (normalizedDate) {
      this.onDateUpdated(normalizedDate);
      this._syncScheduleBySelectedDate();
    } else {
      this.timeRanges = null;
    }

    this.fieldChange$.next(undefined);
    this.isOutletSelectedFlag.set(false);
  }

  public changeRange(range: string) {
    this.selectedTimeRange = range;
    this.fieldChange$.next(undefined);
    this.isOutletSelectedFlag.set(false);
  }

  public calendarFetcher(monthDate: Date, prevCalendar?: DeliveryCalendar) {
    return this.getCalendar({ monthDate, prevCalendar, monthsCount: 2 });
  }

  public async onMonthNavigate(monthDate: string) {
    await this.loadCalendar(monthDate, this.calendar());
  }

  public getCalendar({
    monthDate,
    prevCalendar,
    monthsCount = 1,
  }: {
    monthDate: Date;
    prevCalendar?: DeliveryCalendar;
    monthsCount?: number;
  }): Promise<DeliveryCalendar> {
    return this.orderId
      ? this._deliveryAvailability.getOrderOutletCalendar(
          this.orderId,
          this.outlet.id,
          monthsCount,
          monthDate,
          prevCalendar,
        )
      : this._deliveryAvailability.getOutletCalendar(
          this.bakeryId,
          this.outlet.id,
          monthsCount,
          monthDate,
          prevCalendar,
        );
  }

  public onDateUpdated(date: Date) {
    this.timeRanges = this._deliveryAvailability.getTimeRangesForPickup(this.schedule, date);
  }

  public scheduleChange(dateWithSchedule: any) {
    if (dateWithSchedule && dateWithSchedule.schedule) {
      this.timeRanges = dateWithSchedule.schedule;

      if (!this.selectedTimeRange) {
        this._openRangeDropdown();
      }
    }
  }

  private _openRangeDropdown() {
    if (this._ngZone.isStable) {
      this.rangeDropDown?.open();
      return;
    }

    this.addSubscriber(
      this._ngZone.onStable.pipe(take(1)).subscribe(() => {
        this.rangeDropDown?.open();
      }),
    );
  }

  public contextInit(context) {
    this.dp = context;
  }

  selectAsapTime() {
    this.onAsapSelected.emit({
      outletId: this.outlet.id,
      date: new Date(),
      timeRange: this.asapTime(),
    });
  }

  private async initAsapAvailability() {
    this.asapTime.set(this.isAsapAvailable ? await this.getAsapTime() : null);
  }

  private async getAsapTime(): Promise<string> {
    try {
      const today = new Date();
      const calendar = await this.getCalendar({ monthDate: today });
      const { schedule } = calendar.getDate(today);

      return schedule && schedule.length > 0 ? schedule[0] : null;
    } catch (e) {
      console.error('getAsap', e);
      return '';
    }
  }

  private async loadCalendar(monthDate: string, prevCalendar?: DeliveryCalendar): Promise<void> {
    const fromDate = moment(monthDate, 'YYYY-MM-DD', true).toDate();

    this.isCalendarLoading.set(true);

    try {
      this.calendar.set(await this.calendarFetcher(fromDate, prevCalendar));
      this._syncScheduleBySelectedDate();
    } finally {
      this.isCalendarLoading.set(false);
    }
  }

  private _syncScheduleBySelectedDate() {
    if (!this.selectedDate || !this.calendar() || this.calendar().isDateInactive(this.selectedDate)) {
      return;
    }

    const calendarDay = this.calendar().getDate(this.selectedDate);
    if (calendarDay) {
      this.scheduleChange(calendarDay);
    }
  }

  private _getCalendarRequestDate(): string {
    return this.selectedDate ? moment(this.selectedDate).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD');
  }

  private _subscribe() {
    this.addSubscriber(
      this.fieldChange$.subscribe(() => {
        this.onOutletChange.emit({
          outletId: this.outlet.id,
          date: this.selectedDate,
          timeRange: this.selectedTimeRange,
        });
      }),
    );
  }

  private _tryToDatepickerScroll() {
    setTimeout(() => {
      try {
        if (
          this.selectedDate ||
          !this.dp ||
          !this.datePickerLabel ||
          !this.datePickerLabel.nativeElement ||
          this._uiContext.isMobile() ||
          this.asapTime()
        ) {
          return;
        }

        this.dp.open();
        this._deliveryUi.scrollToDatepicker(this.datePickerLabel, 410, this.containerRef);
      } catch (e) {
        console.error(e);
      }
    }, 700);
  }
}
